import React, { useEffect, useState, useMemo } from 'react';
import { useSelector } from 'umi';
import { Card, Table, Bubble, Modal, Button, notification } from 'tea-component';
import Loading from '@/pages/intelligence/components/Loading';
import useIntlExtend from '@/pages/intelligence/hooks/useIntlExtend';
import SortBy from '@/pages/intelligence/components/TableComponents/components/SortBy';
import CloumsCenter from '@/pages/intelligence/components/TableComponents/components/CloumsCenter';
import classNames from 'classnames';
import SvgIcon from '@/pages/home/components/SvgIcon';
import PublicTrueData, { IPublicTrueDataBtnLogConfig } from '@/pages/intelligence/components/PublicTrueData';
import './index.scss';
import moment from 'moment';
import ImagePreview from '@intelligence/components/ImagePreview';
import { delTrueDataFeedbackHistory } from '@/pages/intelligence/services/topcharts';
import { toDateTypeMomentFormat } from '@/pages/intelligence/utils/common';
import { buriedBtnPoint } from '@/pages/home/utils/buriedPoint';
import { dbiBtnLogMes } from '@intelligence/utils/buriedPointCfg';
import DefaultAvatar from '@/pages/home/assets/img/common/user-default.svg';
import { getTrueDataMetricText } from '@intelligence/components/PublicTrueData/utils/utils';
const { scrollable, autotip, pageable } = Table.addons;

interface pageType {
  pageIndex: number;
  pageSize: number;
}
const NORMALWIDTH = 1740;
const LEFTRIGHTWIDTH = 1717;
const PAGENUM = 5;
const ONCE = 'once';

interface IPublicTrueDataProps extends IBaseProps, IPublicTrueDataBtnLogConfig {}

const PublicTrueDataCollection: React.FC<IPublicTrueDataProps> = (props) => {
  const {
    gameId,
    entityType,
    tableFeedBackData: tableData,
    fetchTrueDataFeedbackHistory,
    pageId,
    tableFeedBackDataTotal = 0,
    feedBackParams,
    openFeedBackTable,
    setOpenFeedBackTable,
    combineGameIdList,
    setGameData,
    gameData,
    selectGameName,
    setSelectGameName,
    setGameIdValue,
    setEntityTypeValue,
    btnLogKeyMap = dbiBtnLogMes,
    publicTrueDataReportBtnLogKey = '',
  } = props;
  const { formatMessage, locale, isEN } = useIntlExtend();
  const { userInfo } = useSelector((state: any) => ({
    userInfo: state?.home?.userInfo,
  }));

  const [outsideFeedBackVisible, setOutsideFeedBackVisible] = useState(false);
  const [isEditFeedBack, setIsEditFeedBack] = useState(false);
  const [editFeedBackRecord, setEditFeedBackRecord] = useState({});
  const [pageSortArr, setPageSortArr] = useState({
    sortKey: 'fill_time',
    sortVal: 'desc',
  });
  const [pageIndex, setPageIndex] = useState(1);
  const [tableLoading, setTableLoading] = useState(false);
  const [visibleImage, setVisibleImage] = useState(false);
  const [visibleDelete, setVisibleDelete] = useState(false);
  const [imageList, setImageList] = useState([]);
  const [deleteId, setDeleteId] = useState(0);
  const [isOverWidth, setIsOverWidth] = useState(false);
  const [isOverRightWidth, setIsOverRightWidth] = useState(false);

  const handleOnChangePageIng = (e: pageType) => {
    setPageIndex(e.pageIndex);

    const params = {
      ...feedBackParams,
      page: e.pageIndex,
      page_size: e.pageSize,
    };

    fetchTrueDataFeedbackHistory(params);
  };

  useEffect(() => {
    fetchTrueDataFeedbackHistory(feedBackParams);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setPageIndex(feedBackParams.page || 1);
  }, [feedBackParams]);

  const changeMpTableSort = (e: any) => {
    setPageSortArr(() => ({
      sortKey: e.key,
      sortVal: e.sortVal,
    }));
    setPageIndex(1);
    const params = {
      ...feedBackParams,
      order_metric: e.key,
      order: e.sortVal,
      page: 1,
    };

    fetchTrueDataFeedbackHistory(params);
  };

  // 关闭图片预览
  const handleCloseImagePreview = () => {
    setVisibleImage(false);
    setImageList([]);
  };

  // 打开图片预览
  const handleClickImageList = (imgList: any) => {
    setImageList(imgList);
    setVisibleImage(true);
  };

  // 打开link
  const handleClickLink = (url: string) => {
    // console.log('handleClickLink-url',url);
    window.open(url, '_blank');
  };

  // 删除反馈
  const handleClickDeleteIcon = (id: any) => {
    setDeleteId(id);
    setVisibleDelete(true);
  };

  // 删除反馈
  const handleClickDelete = async () => {
    if (deleteId) {
      const params = {
        id: Number(deleteId),
      } as any;
      try {
        await delTrueDataFeedbackHistory(params);
        fetchTrueDataFeedbackHistory(feedBackParams);

        notification.success({
          unique: true,
          description: formatMessage({
            id: 'dbi_success_opt',
            defaultMessage: 'Success Delete!',
          }),
          duration: 1000,
        });
      } catch (err) {
        notification.error({
          unique: true,
          description: formatMessage({
            id: 'dbi_feedback_delete_failed',
            defaultMessage: 'Failed Delete!',
          }),
          duration: 1000,
        });
      } finally {
      }
    }
    setVisibleDelete(false);
  };

  // 编辑反馈
  const handleClickEdit = (record: any) => {
    setEditFeedBackRecord(record);
    setIsEditFeedBack(true);
    setOutsideFeedBackVisible(true);
  };

  // 新增反馈
  const handleClickAdd = () => {
    setEditFeedBackRecord({});
    setIsEditFeedBack(false);
    setOutsideFeedBackVisible(true);
    // 埋点
    const publicTrueDataReportBtnLogConfig = btnLogKeyMap?.[publicTrueDataReportBtnLogKey];
    if (publicTrueDataReportBtnLogConfig) {
      const extInfo = publicTrueDataReportBtnLogConfig.extInfo || '';
      const extInfoNew = !gameId ? extInfo : `${extInfo}: (${gameId})`;
      buriedBtnPoint({ ...publicTrueDataReportBtnLogConfig, extInfo: extInfoNew });
    }
  };

  // 根据浏览器大小设置宽表宽度配置
  useEffect(() => {
    setIsOverWidth(window.innerWidth > NORMALWIDTH);
    setIsOverRightWidth(window.innerWidth > LEFTRIGHTWIDTH);
    const handleWindowResize = () => {
      setIsOverWidth(window.innerWidth > NORMALWIDTH);
      setIsOverRightWidth(window.innerWidth > LEFTRIGHTWIDTH);
    };
    window.addEventListener('resize', handleWindowResize);
    return () => window.removeEventListener('resize', handleWindowResize);
  }, []);
  const NORMALTYPE: any = {
    combine: formatMessage({ id: 'dbi_pcconsole_games' }),
    console: formatMessage({ id: 'dbi_console_games' }),
    pc: formatMessage({ id: 'dbi_pc_games' }),
    mobile: formatMessage({ id: 'dbi_mobile_games' }),
    both: formatMessage({ id: 'dbi_mobile_combine' }),
  };
  const renderType = () => {
    const data = combineGameIdList.filter(
      (item: any) => item.entity_type === 'combine' || item.entity_type === 'mobile',
    );
    if (data.length > 1) {
      return 'both';
    }
    return data[0]?.entity_type || '';
  };
  const columns = [
    {
      key: 'metric_date',
      header: (
        <CloumsCenter align={'left'} width={'100%'} classNameValue="dbi-justify-content-normal">
          <SortBy
            onChange={changeMpTableSort}
            title={formatMessage({ id: 'dbi_date' })}
            list={[
              {
                key: 'metric_date',
                sortMetric: 'metric_date',
                text: formatMessage({ id: 'dbi_date' }),
                title: formatMessage({ id: 'dbi_date' }),
                ratioType: 0,
              },
            ]}
            defaultKey={pageSortArr.sortKey}
            defaultValue={pageSortArr.sortVal}
          />
        </CloumsCenter>
      ),
      // width: isOverWidth ? '15%' : 210,
      // width: '12%',
      width: 210,
      align: 'left',
      render: (record: any) => <span>{record.metric_date || '-'}</span>,
    },
    {
      key: 'metric_name',
      header: (
        <CloumsCenter align={'left'} width={'100%'} classNameValue="dbi-justify-content-normal">
          <SortBy
            onChange={changeMpTableSort}
            title={formatMessage({ id: 'dbi_top_metric' })}
            list={[
              {
                key: 'metric_name',
                sortMetric: 'metric_name',
                text: formatMessage({ id: 'dbi_top_metric' }),
                title: formatMessage({ id: 'dbi_top_metric' }),
                ratioType: 0,
              },
            ]}
            defaultKey={pageSortArr.sortKey}
            defaultValue={pageSortArr.sortVal}
          />
        </CloumsCenter>
      ),
      // width: isOverWidth ? '12.86%' : 180,
      width: 180,
      // minWidth: 180,
      align: 'left',
      render: (record: any) => <span>{getTrueDataMetricText(record.metric_name)}</span>,
    },
    {
      key: 'metric_value',
      header: formatMessage({ id: 'dbi_data' }),
      // width: isOverWidth ? '7.14%' : 100,
      width: 180,
      // minWidth: 100,
      align: 'left',
      render: (record: any) => {
        if (!record.metric_value) {
          return '-';
        }
        return (
          <div className="dbi_table_public_data dbi_table_public_data_content">
            <Bubble arrowPointAtCenter dark content={record.metric_value}>
              <div className={'dbi_bubble_show dbi_table_public_data dbi_table_public_data_content'}>
                <span>{record.metric_value}</span>
              </div>
            </Bubble>
          </div>
        );
      },
      // render: (record: any) => <span>{record.metric_value || '-'}</span>,
    },
    {
      key: 'source_type',
      header: (
        <CloumsCenter align={'left'} width={'100%'} classNameValue="dbi-justify-content-normal">
          <SortBy
            onChange={changeMpTableSort}
            title={formatMessage({ id: 'dbi_feedback_source' })}
            list={[
              {
                key: 'source_type',
                sortMetric: 'source_type',
                text: formatMessage({ id: 'dbi_feedback_source' }),
                title: formatMessage({ id: 'dbi_feedback_source' }),
                ratioType: 0,
              },
            ]}
            defaultKey={pageSortArr.sortKey}
            defaultValue={pageSortArr.sortVal}
          />
        </CloumsCenter>
      ),
      // width: isOverWidth ? '13.57%' : 190,
      width: 180,
      // minWidth: 190,
      align: 'left',
      render: (record: any) => {
        const imgList = record.signature_source_image ? record.signature_source_image?.split('|') : [];
        if (!record.source_type) {
          return '-';
        }
        const sourceTypeMap: any = {
          'Public Data': formatMessage({ id: 'dbi_feedback_public_data' }),
          'Expert Knowledge': formatMessage({ id: 'dbi_feedback_expert_knowledge' }),
          'Financial Reports': formatMessage({ id: 'dbi_feedback_financial_reports' }),
          Others: formatMessage({ id: 'dbi_feedback_public_others' }),
        };
        return (
          <div className="dbi_table_public_data">
            {sourceTypeMap[record.source_type]}
            {imgList?.length ? (
              <SvgIcon
                className="dbi-table-icon-image-preview"
                type="image-preview"
                onClick={(e) => {
                  e?.stopPropagation();
                  e?.preventDefault();
                  handleClickImageList(imgList);
                }}
              />
            ) : null}
          </div>
        );
      },
    },
    {
      key: 'url',
      header: formatMessage({ id: 'dbi_feedback_link' }),
      // width: isOverWidth ? '6%' : 84,
      width: 120,
      // minWidth: 70,
      align: 'left',
      render: (record: any) => {
        if (!record.url) {
          return '-';
        }
        return (
          <div>
            {record?.source_type === 'Expert Knowledge' ? (
              record.url
            ) : (
              <SvgIcon
                type={'aigc-share'}
                className={`dbi-table-icon-link`}
                onClick={(e) => {
                  e?.stopPropagation();
                  e?.preventDefault();
                  handleClickLink(record.url);
                }}
              />
            )}
          </div>
        );
      },
    },
    {
      key: 'user_name',
      header: (
        <CloumsCenter align={'left'} width={'100%'} classNameValue="dbi-justify-content-normal">
          <SortBy
            onChange={changeMpTableSort}
            title={formatMessage({ id: 'dbi_feedback_contributor' })}
            list={[
              {
                key: 'user_name',
                sortMetric: 'user_name',
                text: formatMessage({ id: 'dbi_feedback_contributor' }),
                title: formatMessage({ id: 'dbi_feedback_contributor' }),
                ratioType: 0,
              },
            ]}
            defaultKey={pageSortArr.sortKey}
            defaultValue={pageSortArr.sortVal}
          />
        </CloumsCenter>
      ),
      // width: isOverWidth ? '12.14%' : 170,
      width: 170,
      // minWidth: 170,
      align: 'left',
      render: (record: any) => (
        <span style={{ display: 'flex', alignItems: 'center' }}>
          <img
            src={record.user_cover || DefaultAvatar}
            onError={(e) => { e.currentTarget.src = DefaultAvatar; }}
            style={{ width: 40, height: 40, marginRight: 8, borderRadius: 50, objectFit: 'cover' }}
          />
          <span>{record?.anonymity ? formatMessage({ id: 'dbi_EK_user' }) : record.user_name || '-'}</span>
        </span>
      ),
    },
    {
      key: 'fill_time',
      header: (
        <CloumsCenter align={'left'} width={'100%'} classNameValue="dbi-justify-content-normal">
          <SortBy
            onChange={changeMpTableSort}
            title={formatMessage({ id: 'dbi_feedback_contributor_time' })}
            list={[
              {
                key: 'fill_time',
                sortMetric: 'fill_time',
                text: formatMessage({ id: 'dbi_ddbi_feedback_contributor_timeate' }),
                title: formatMessage({ id: 'dbi_feedback_contributor_time' }),
                ratioType: 0,
              },
            ]}
            defaultKey={pageSortArr.sortKey}
            defaultValue={pageSortArr.sortVal}
          />
        </CloumsCenter>
      ),
      // width: isOverWidth ? '13.57%' : 190,
      width: 190,
      // minWidth: 190,
      align: !isOverRightWidth && isEN ? 'right' : 'left',
      render: (record: any) => <span>{record.fill_time || '-'}</span>,
    },
    // {
    //   key: 'game_type',
    //   header: (
    //     <CloumsCenter align={'left'} width={'100%'} classNameValue="dbi-justify-content-normal">
    //       <SortBy
    //         onChange={changeMpTableSort}
    //         title={formatMessage({ id: 'dbi_type' })}
    //         list={[
    //           {
    //             key: 'game_type',
    //             sortMetric: 'game_type',
    //             text: formatMessage({ id: 'dbi_type' }),
    //             title: formatMessage({ id: 'dbi_type' }),
    //             ratioType: 0,
    //           },
    //         ]}
    //         defaultKey={pageSortArr.sortKey}
    //         defaultValue={pageSortArr.sortVal}
    //       />
    //     </CloumsCenter>
    //   ),
    //   // width: isOverWidth ? '15%' : 210,
    //   width: 210,
    //   // minWidth: 210,
    //   align: 'left',
    //   render: (record: any) => <span>{NORMALTYPE[renderType()] || record.game_type}</span>,
    // },
    {
      key: 'content',
      header: formatMessage({ id: 'dbi_feedback_note' }),
      // width: isOverWidth ? '15.58%' : 218,
      width: 180,
      // minWidth: 181,
      align: 'left',
      render: (record: any) => {
        if (!record.content) {
          return '-';
        }
        return (
          <div className="dbi_table_public_data dbi_table_public_data_content">
            <Bubble arrowPointAtCenter dark content={record.content}>
              <div className={'dbi_bubble_show dbi_table_public_data dbi_table_public_data_content'}>
                <span>{record.content}</span>
              </div>
            </Bubble>
          </div>
        );
      },
    },
    {
      key: 'options',
      header: formatMessage({ id: 'Action' }),
      // width: isOverWidth ? '4.14%' : 58,
      width: 95,
      // minWidth: 58,
      align: 'left',
      render: (record: any) => (
        <div className="dbi_table_public_data dbi_table_public_data_options">
          {userInfo?.userid === record?.user_id && (
            <>
              <Bubble arrowPointAtCenter dark content={formatMessage({ id: 'dbi_feedback_edit_public_true_data' })}>
                <div className={'dbi_bubble_feedback_option_icon'}>
                  <SvgIcon
                    type={'pencil-m'}
                    className={`dbi-table-icon-pencil-m dbi-table-icon-hover-blue`}
                    onClick={(e) => {
                      e?.stopPropagation();
                      e?.preventDefault();
                      handleClickEdit(record);
                    }}
                  />
                </div>
              </Bubble>
              <Bubble arrowPointAtCenter dark content={formatMessage({ id: 'dbi_delete' })}>
                <div className={'dbi_bubble_feedback_option_icon dbi_bubble_show-right'}>
                  <SvgIcon
                    type={'clear-icon'}
                    className={`dbi-table-icon-star-delete dbi-table-icon-hover-blue`}
                    onClick={(e) => {
                      e?.stopPropagation();
                      e?.preventDefault();
                      handleClickDeleteIcon(record?.id);
                    }}
                  />
                </div>
              </Bubble>
            </>
          )}
        </div>
      ),
    },
  ];

  const handleClickOpenTable = () => {
    setOpenFeedBackTable(!openFeedBackTable);
  };

  // 是否需要展示
  const isShowTable = useMemo(() => tableFeedBackDataTotal > 0, [tableFeedBackDataTotal]);

  const getMetricTimeCur = (tableItem: any) => {
    const formatValue = toDateTypeMomentFormat[tableItem.granularity] || 'YYYY-MM-DD';
    if (tableItem.metric_start_date === ONCE) {
      return tableItem.metric_end_date || formatMessage({ id: 'dbi_feedback_once' }); //
    }
    if (tableItem.metric_start_date === '') {
      return '-';
    }
    if (tableItem.metric_start_date === tableItem.metric_end_date) {
      return moment(tableItem.metric_start_date).format(formatValue);
    }
    return `${moment(tableItem.metric_start_date).format(formatValue)}~${moment(tableItem.metric_end_date).format(
      formatValue,
    )}`;
  };

  // 宽表数据格式转换
  const tableDataCur = useMemo(() => {
    const tableDataValue = tableData?.map((item: any) => ({
      ...item,
      metric_date: getMetricTimeCur(item),
      fill_time: item.fill_time ? moment(item.fill_time).format('YYYY-MM-DD') : '',
    }));
    return tableDataValue || [];
  }, [tableData, locale]);

  return (
    <>
      {isShowTable ? (
        <>
          <Card
            className={classNames('dbi-public-true-data-collection', {
              'dbi-public-true-data-collection-active': openFeedBackTable,
            })}
          >
            <Card.Body
              title={
                <>
                  <div className="performance-felx">
                    <div className="dbi-card-title-left" onClick={handleClickOpenTable}>
                      <span className="dbi-public-true-data-collection-spantitle">
                        <SvgIcon
                          className={classNames('dbi-feedback-fill-icon-arrow-right', {
                            'dbi-feedback-fill-icon-arrow-active': openFeedBackTable,
                          })}
                          type="fill-icon-arrow"
                        />
                        <span className="performance-felx-text">
                          {`${formatMessage({
                            id: 'dbi_top_public_true_data_collection',
                          })} (${tableFeedBackDataTotal})`}
                        </span>
                      </span>
                    </div>
                    <div className="dbi-card-title-right">
                      <Bubble
                        arrowPointAtCenter
                        placement="top-end"
                        dark
                        content={formatMessage({ id: 'dbi_feedback_add_public_true_data' })}
                      >
                        <div>
                          <SvgIcon type="cross" className="dbi-public-collection-cross" onClick={handleClickAdd} />
                        </div>
                      </Bubble>
                    </div>
                  </div>
                </>
              }
            >
              {openFeedBackTable && (
                <>
                  {tableLoading ? (
                    <Loading content={formatMessage({ id: 'common_loading' })} style={{ height: 400, color: '#444' }} />
                  ) : (
                    <Table
                      className={'dbi_kpioverview_table'}
                      records={tableDataCur}
                      columns={columns}
                      addons={[
                        pageable({
                          pageIndex,
                          onPagingChange: handleOnChangePageIng,
                          stateText: formatMessage(
                            { id: 'dbi_pagination_total' },
                            { total: tableFeedBackDataTotal ?? 0 },
                          ),
                          stateTextVisible: tableFeedBackDataTotal,
                          pageSizeVisible: false,
                          pageIndexVisible: tableFeedBackDataTotal > PAGENUM,
                          jumpVisible: tableFeedBackDataTotal > PAGENUM,
                          endJumpVisible: tableFeedBackDataTotal > PAGENUM,
                          pageSizeOptions: [5],
                          recordCount: tableLoading ? 0 : tableFeedBackDataTotal,
                        }),
                        scrollable({
                          minWidth: 400,
                        }),
                        autotip({
                          emptyText: formatMessage({ id: 'dbi_detail_no_data_under' }),
                        }),
                        // }),
                      ]}
                    />
                  )}
                </>
              )}
            </Card.Body>
          </Card>
          {outsideFeedBackVisible && (
            <PublicTrueData
              gameId={gameId}
              entityType={entityType}
              btnLogKeyMap={btnLogKeyMap}
              publicTrueDataReportBtnLogKey={publicTrueDataReportBtnLogKey}
              fetchTrueDataFeedbackHistory={fetchTrueDataFeedbackHistory}
              pageId={pageId}
              isNeedSearchGame={true}
              setGameData={setGameData}
              gameData={gameData}
              selectGameName={selectGameName}
              match={{ ...props?.match, entityType }}
              setSelectGameName={setSelectGameName}
              isEdit={isEditFeedBack}
              editRecord={editFeedBackRecord}
              outsideFeedBackVisible={outsideFeedBackVisible}
              isOutsideOpen={true}
              setOutsideFeedBackVisible={setOutsideFeedBackVisible}
              feedBackParams={feedBackParams}
              buttonNoShow={true}
              isGameDetail={true}
              setGameIdValue={setGameIdValue}
              setEntityTypeValue={setEntityTypeValue}
            />
          )}
          {visibleImage && (
            <ImagePreview visible={visibleImage} imageList={imageList} onClose={handleCloseImagePreview} />
          )}
          {visibleDelete && (
            <Modal
              className="dbi-delete-group"
              visible={visibleDelete}
              size={500}
              onClose={() => setVisibleDelete(false)}
            >
              <Modal.Body>
                <div className="dbi-delete-group-title">
                  {formatMessage({ id: 'dbi_feedback_you_sure_delete_true_data' })}
                </div>
              </Modal.Body>
              <Modal.Footer>
                <Button type="primary" onClick={handleClickDelete}>
                  {formatMessage({ id: 'dbi_confirm' })}
                </Button>
                <Button
                  type="weak"
                  onClick={() => {
                    setVisibleDelete(false);
                  }}
                >
                  {formatMessage({ id: 'dbi_cancel' })}
                </Button>
              </Modal.Footer>
            </Modal>
          )}
        </>
      ) : null}
    </>
  );
};

export default PublicTrueDataCollection;
